<template>
  <!-- amount -->
  <div>
    <div
      v-if="transactionInfo.transferNeedDocumentUpload"
      class="attention-box"
    >
      <div>
        <span class="flex justify-center text font-bold text-center"
          >Attention:</span
        >
        <p class="text text-sm font-normal" style="word-break: normal">
          Dear customer, in order to complete this transaction, we'll require
          you to upload some extra documents.
          <a href="#fileInput" class="underline font-bold cursor-pointer">
            Upload
          </a>
        </p>
      </div>
    </div>
    <div
      class="flex flex-col items-center justify-center rounded-lg shadow-xs border px-5 py-5 transition-all duration-300 ease-linear"
    >
      <p class="font-light text-xs lg:text-sm">Amount</p>
      <h3 class="text-2xl mt-1">
        {{
          transactionInfo.depositMethod === $transactionType.ASSET_TRANSFER
            ? parseMoneyAll(
                transactionInfo.transferAmount.amount +
                  (transactionInfo.businessCommission || 0),
                transactionInfo.transferAmount.currency
              )
            : transactionInfo.depositMethod === $transactionType.LOCAL_TRANSFER
            ? parseMoneyAll(
                transactionInfo.transferAmount.amount +
                  (transactionInfo.businessCommission || 0),
                transactionInfo.transferAmount.currency
              )
            : parseMoneyAll(
                (transactionInfo.transferAmount.amount ||
                  transactionDetails.amount) +
                  (transactionInfo.businessCommission || 0),
                transactionInfo.transferAmount.currency
              )
        }}
      </h3>
      <p class="flex items-center gap-x-3 mt-2">
        <span class="border-b border-solid border-gray-200 w-10 lg:w-24"></span>
        <span
          @click="toggleShow('showBreakDown')"
          class="font-light text-xs lg:text-sm text-primary flex items-center gap-x-1 cursor-pointer"
          ><span>See Breakdown</span>
          <div
            class="transition-all duration-300 transform"
            :class="showBreakDown ? '-rotate-90' : 'rotate-90'"
          >
            <arrow-right-icon />
          </div>
        </span>
        <span class="border-b border-solid w-10 lg:w-24 border-gray-200"></span>
      </p>
      <transition name="fade-in">
        <ul
          v-if="showBreakDown"
          class="w-full text-xs lg:text-sm font-light px-2 lg:px-4 mt-4 text-root-4"
        >
          <li
            v-if="transactionInfo.transferFee"
            class="flex items-center justify-between mb-4"
          >
            <span
              >Our fees
              {{
                transactionInfo.transferFeePercentage
                  ? `${transactionInfo.transferFeePercentage}%`
                  : ''
              }}</span
            >
            <span>{{
              `${transactionInfo.transferFee.currency} ${addComma(
                transactionInfo.transferFee.amount
              )}`
            }}</span>
          </li>
          <li class="flex items-center justify-between mb-4">
            <span>Amount converted</span>
            <span>
              {{
                transactionInfo.depositMethod ===
                $transactionType.LOCAL_TRANSFER
                  ? transactionInfo.localCurrencyDepositAmount.currency
                  : transactionInfo.depositMethod ===
                    $transactionType.ASSET_TRANSFER
                  ? transactionInfo.assetDepositAmount.currency
                  : transactionInfo.transferDeposit.currency
              }}
              {{
                addComma(
                  (
                    (transactionInfo.depositMethod ===
                    $transactionType.LOCAL_TRANSFER
                      ? transactionInfo.localCurrencyDepositAmount.amount
                      : transactionInfo.depositMethod ===
                        $transactionType.ASSET_TRANSFER
                      ? transactionInfo.assetDepositAmount.amount
                      : transactionInfo.depositMethod ===
                        $transactionType.WIRE_TRANSFER
                      ? transactionInfo.fxCurrencyDepositAmount.amount
                      : transactionInfo.transferDeposit.amount || '-') -
                    transactionInfo.transferFee.amount
                  ).toFixed(2)
                )
              }}</span
            >
          </li>
          <li class="flex items-center justify-between mb-4">
            <span>Exchange rate</span>
            <!-- <span>{{ transactionDetails.exchangeRate }}</span> -->
            <div v-if="!isHoldingsTransfer">
              <div v-if="rateIncreased">
                <div class="text-root-4">
                  {{
                    transactionInfo.depositMethod ===
                    $transactionType.LOCAL_TRANSFER
                      ? transactionDetails.localDepositUpdatedDetails.from
                      : transactionInfo.fxDepositUpdatedDetails.from
                  }}
                  {{
                    transactionInfo.depositMethod ===
                    $transactionType.LOCAL_TRANSFER
                      ? transactionInfo.localDepositUpdatedDetails.exchange_rate
                      : transactionInfo.fxDepositUpdatedDetails.exchange_rate
                  }}
                </div>
                <span class="text-root-1 line-through">
                  {{
                    transactionInfo.transferInstruction.division_operator
                      ? transactionInfo.transferInstruction.from
                      : transactionInfo.transferInstruction.to
                  }}
                  {{ transactionInfo.transferInstruction.exchange_rate }}
                </span>
              </div>
              <div v-else class="text-root-4">
                {{
                  transactionInfo.transferInstruction.division_operator
                    ? transactionInfo.transferInstruction.from
                    : transactionInfo.transferInstruction.to
                }}
                {{ transactionInfo.transferInstruction.exchange_rate }}
              </div>
            </div>
            <p v-else>
              {{ transactionInfo.transferExchangeRate }}
            </p>
          </li>
          <li class="flex items-center justify-between mb-4">
            <span>Recipient would receive</span>
            <span class="font-semibold">{{
              parseMoneyAll(
                transactionInfo.transferAmount.amount,
                transactionInfo.transferAmount.currency
              )
            }}</span>
          </li>
        </ul>
      </transition>
    </div>
    <!-- transfer progress -->
    <div class="mt-5">
      <h3 class="text-sm lg:text-base font-semibold">Transfer Progress</h3>
      <div class="border rounded-lg shadow-xs mt-4 p-6 relative">
        <div class="flex gap-x-5">
          <p
            class="text-white text-xs h-5 w-5 lg:h-8 lg:w-8 flex items-center justify-center rounded-full"
            :class="
              isStatusProcessing ? 'bg-primary' : 'border-2 bg-white z-10'
            "
          >
            &#10003;
          </p>
          <div
            class="w-full flex flex-col lg:flex-row gap-y-5 lg:justify-between"
          >
            <div>
              <h4 class="font-semibold text-xs lg:text-sm">
                Deposit
                <span v-if="transactionInfo.depositMethod" class="capitalize"
                  >({{ formatServerName(transactionInfo.depositMethod) }})</span
                >
              </h4>
              <span
                class="inline-block text-xs font-normal rounded-2xl px-2 py-1 capitalize mt-2"
                :class="
                  statusClass(
                    isHoldingsTransfer
                      ? $STATUS.COMPLETED
                      : transactionInfo.depositStatus
                  )
                "
              >
                {{
                  isAwaitingDeposit
                    ? 'awaiting deposit'
                    : isHoldingsTransfer
                    ? $STATUS.COMPLETED
                    : formatServerName(transactionInfo.depositStatus)
                }}
              </span>
            </div>
            <div
              v-if="isAwaitingDeposit"
              @click="handleDepositPage"
              class="flex items-center justify-center py-2 px-4 gap-x-3 mb-6 bg-info-subtle rounded-lg shadow-xs cursor-pointer"
            >
              <eye-icon :color="$primaryColor" />
              <p class="font-light text-primary text-xs lg:text-sm">
                {{
                  transactionInfo.depositStatus === $STATUS.CANCELLED
                    ? 'Retry Deposit'
                    : 'Deposit Details'
                }}
              </p>
            </div>
            <div
              class="text-xs capitalize font-medium mt-8"
              v-if="isCompletedDeposit"
            >
              {{
                parseRelativeTime(
                  transactionInfo.localDepositUpdatedAt ||
                    transactionInfo.fxDepositUpdatedAt ||
                    transactionInfo.assetDepositUpdatedAt
                )
              }}
            </div>
          </div>
        </div>
        <div class="flex gap-x-5 mt-16">
          <p
            class="border-2 h-5 w-5 lg:h-8 lg:w-8 flex items-center justify-center rounded-full -mt-1 lg:mt-0"
          ></p>
          <div
            class="flex flex-col lg:flex-row gap-y-5 lg:justify-between w-full"
          >
            <div>
              <h4 class="font-semibold text-xs lg:text-sm">
                Send To Recipient
              </h4>
              <span
                class="inline-block text-xs font-normal rounded-2xl px-3 py-1 capitalize mt-2"
                :class="statusClass(transactionInfo.transferStatus)"
              >
                {{ formatServerName(transactionInfo.transferStatus) }}
              </span>
            </div>
            <div
              @click="changePage"
              class="flex items-center justify-center py-2 px-4 gap-x-2 mb-6 bg-info-subtle rounded-lg shadow-xs cursor-pointer"
            >
              <eye-icon :color="$primaryColor" />
              <p class="font-light text-primary text-xs lg:text-sm">
                Recipient Details
              </p>
            </div>
          </div>
        </div>
        <!-- blue border -->
        <div
          class="absolute top-10 lg:top-12 left-8 lg:left-10 border-l-2"
          :class="isAwaitingDeposit ? ' h-44 lg:h-25' : 'border-primary h-24'"
        ></div>
      </div>
    </div>
    <!-- estimated time of arrival -->
    <div class="mt-5">
      <p
        class="flex items-center justify-between bg-info-subtle py-3.5 px-5 text-primary rounded-lg shadow-xs font-light lg:text-sm text-xs"
      >
        <span>Estimated Time of Arrival</span>
        <span class="font-semibold">In 2 days</span>
        <!-- <span class="font-semibold"> {{ transactionDetails.id }}</span> -->
      </p>
    </div>
    <div v-if="transactionInfo.transferNeedDocumentUpload && hasDocumentType">
      <div v-for="(document, index) in hasRequestDocUploadArray" :key="index">
        <el-form
          label-position="top"
          :model="uploadDocumentForm"
          ref="uploadDocumentForm"
          size="normal"
        >
          <el-form-item prop="transfer_document">
            <div class="flex items-center pt-5 gap-x-1 pb-2">
              <span class="font-normal text-sm"
                >Upload Relevant Document :{{
                  formatServerName(document.type)
                }}</span
              >
              <el-tooltip placement="top">
                <div slot="content" class="text-xs py-2">
                  For example: <br />1. Transaction invoice ( such as a supplier
                  or manufacturer invoice ) <br />2. Trade agreement between you
                  and beneficiary <br />3. A contract between you and
                  beneficiary <br />4. Demand notice.
                </div>
                <span> <muted-info-icon /> </span>
              </el-tooltip>
            </div>
            <el-input
              v-model="uploadDocumentForm.transfer_document"
              type="file"
              name="documentFile"
              accept=".jpg,.jpeg,.png,.pdf,.docx,.doc,.csv,.xlsx"
              placeholder="Upload Document"
              @change="handleChange(document.id)"
              :id="document.id"
            >
              <i
                slot="prefix"
                class="text-fg-muted absolute top-1 left-4 overflow-x-scroll truncate w-16 lg:w-24"
                >{{ fileName }}</i
              >
              <i
                slot="suffix"
                class="flex items-center justify-center w-40 h-10 absolute right-0 top-2 cursor-pointer fg-on-disabled rounded-lg -z-10 browseButton"
              >
                <span class="text-white text-xs mr-2 ml-2">Choose file</span>
              </i>
            </el-input>
          </el-form-item>
          <div
            v-if="uploadDocumentForm.transfer_document"
            class="flex gap-x-1 justify-end"
          >
            <el-button
              class="py-2.5 px-6 border rounded-lg cursor-pointer"
              @click="resetForm('uploadDocumentForm')"
              >Cancel</el-button
            >
            <el-button
              :isLoading="isLoading"
              class="py-2.5 px-6 rounded-lg justify-center cursor-pointer items-center bg-primary text-white"
              @click.prevent="validateForm('uploadDocumentForm', document.id)"
            >
              {{ isLoading ? 'saving' : 'Save' }}
            </el-button>
          </div>
          <spinner v-if="isLoading" />
        </el-form>
      </div>
    </div>
    <div></div>
    <ul class="font-light text-xs lg:text-sm px-5">
      <div>
        <div v-for="(document, index) in transferDocuments" :key="index">
          <div
            v-if="
              document.questionnaire &&
              document.questionnaire.length > 0 &&
              hasNonEmptyQuestions(document)
            "
          >
            <div
              v-if="document.status === $STATUS.PENDING"
              class="flex justify-between items-center border-b py-3.5 lg:py-5"
            >
              <p>
                <span class="capitalize"> Questionnaire </span>
              </p>
              <p
                class="capitalize bg-pink-2 text-pink-3 cursor-pointer flex items-center rounded-full py-2 px-5"
                @click="handleQuestionaireForm(document)"
              >
                Attention
                <span> <arrow-right-icon color="#D71841" /></span>
              </p>
            </div>
            <div
              v-else
              class="flex justify-between items-center border-b py-3.5 lg:py-5"
            >
              <p>
                <span class="capitalize"> Questionnaire </span>
              </p>
              <div
                @click="viewQuestionaireDetails(document)"
                class="cursor-pointer"
              >
                <div
                  class="disputed_status capitalize"
                  v-if="document.status === $STATUS.IN_DISPUTE"
                >
                  Disputed
                </div>
                <arrow-right-icon v-else :color="$primaryColor" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="relevantDocuments.length > 0">
        <li class="flex justify-between items-center border-b py-3.5 lg:py-5">
          <span>Relevant Document</span>
          <span
            class="text-primary font-semibold text-xl cursor-pointer"
            @click="toggleShow('showTransferDocument')"
            >{{ showTransferDocument ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showTransferDocument">
            <div v-for="(document, index) in relevantDocuments" :key="index">
              <p
                class="capitalize bg-info-subtle mb-2 rounded-sm py-2.5 col-span-5 px-2 text-sm"
              >
                <span class="text-primary font-bold"> Document Type :</span>
                <span class="text-primary">
                  {{ formatServerName(document.type) }}
                </span>
              </p>
              <div
                v-if="hasPDF(document.fileURL)"
                class="h-32 my-2 rounded-lg border flex justify-center items-center border-gray-200"
              >
                <a
                  :href="document.fileURL || '#'"
                  target="_blank"
                  download
                  rel="noopener noreferrer"
                >
                  <div
                    class="cursor-pointer my-5 flex justify-center items-center"
                  >
                    <div>
                      <pdf-icon class="mx-auto" />
                      <span class="text-primary text-sm text-center"
                        >Download PDF</span
                      >
                    </div>
                  </div>
                </a>
              </div>
              <div
                v-else
                class="items-center border-b py-3.5 lg:py-5 px-5 relative bg-gray-100 rounded-lg"
              >
                <div class="demo-image__preview">
                  <el-image
                    style="width: 80%; height: 100px"
                    :src="document.fileURL"
                    :preview-src-list="[document.fileURL]"
                    fit="cover"
                    :zoom-rate="1.2"
                    lazy
                  >
                    <div slot="error" class="flex justify-center image-slot">
                      <i class="el-icon-picture-outline"></i>
                    </div>
                  </el-image>
                </div>
              </div>
            </div>
          </div>
        </transition>
      </div>
      <li class="grid grid-cols-2 items-center border-b py-3.5 lg:py-6">
        <span>Time & Date</span>
        <span class="">{{ displayTimeDate }}</span>
      </li>
      <li class="grid grid-cols-2 items-center border-b py-3.5 lg:py-6">
        <span>Transaction ID</span>
        <span
          >{{ maskID(transactionInfo.transferID) }}
          <span
            class="cursor-pointer mb-1 ml-2"
            v-clipboard:copy="transactionInfo.transferID"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
          >
            <copy-icon />
          </span>
        </span>
      </li>
      <li
        v-if="transactionReference"
        class="grid grid-cols-2 items-center border-b py-3.5 lg:py-6"
      >
        <span>Transaction Reference</span>
        <span>
          {{ maskID(transactionReference) }}
          <span
            class="cursor-pointer mb-1 ml-2"
            v-clipboard:copy="transactionReference"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError"
          >
            <copy-icon />
          </span>
        </span>
      </li>
      <li
        v-if="transactionInfo.depositMethod"
        class="grid grid-cols-2 items-center border-b py-3.5 lg:py-6"
      >
        <span>Payment Method</span>
        <span class="flex items-center gap-x-2">
          <span class="capitalize">{{
            formatServerName(transactionInfo.depositMethod)
          }}</span>
        </span>
      </li>
      <li
        v-if="computePayoutMethod(transactionInfo)"
        class="grid grid-cols-2 items-center border-b py-3.5 lg:py-6"
      >
        <span>Payout Method</span>
        <span class="flex items-center gap-x-2">
          <span class="capitalize">{{
            computePayoutMethod(transactionInfo)
          }}</span>
        </span>
      </li>
      <!-- reason -->
      <li
        class="flex justify-between items-center border-b py-3.5 lg:py-5 cursor-pointer"
        @click="toggleShow('showReasonForTransfer')"
      >
        <span>Reason for transfer</span>
        <span class="text-primary font-semibold text-xl">{{
          showReasonForTransfer ? '-' : '+'
        }}</span>
      </li>
      <transition name="slide-fade">
        <div v-if="showReasonForTransfer">
          <li class="items-center border-b py-3.5 lg:py-5 bg-gray-100 px-3">
            <span style="word-break: normal">{{
              transactionInfo.transferReason
            }}</span>
          </li>
        </div>
      </transition>
      <div>
        <li
          class="grid grid-cols-2 items-center font-normal text-sm border-b py-3.5 lg:py-6"
        >
          <span>From</span>
          <span class="flex items-center gap-x-2">
            <currency-icons
              :currency="
                getCurrencyCountry(transactionInfo.transferDeposit.currency)
              "
            />
            <span class="capitalize">{{
              transactionInfo.transferDeposit.currency
            }}</span>
          </span>
        </li>
        <li
          class="grid grid-cols-2 items-center border-b font-normal text-sm py-3.5 lg:py-6"
        >
          <span>To</span>
          <span class="flex items-center gap-x-2">
            <currency-icons
              :currency="
                getCurrencyCountry(transactionInfo.transferredAmount.currency)
              "
            />
            <span class="capitalize">{{
              transactionInfo.transferredAmount.currency
            }}</span>
          </span>
        </li>
      </div>
      <li class="flex justify-between items-center border-b py-3.5 lg:py-6">
        <span>Recipient Details</span>
        <arrow-right-icon @click.native="changePage" class="cursor-pointer" />
      </li>
      <li
        class="flex justify-between items-center border-b py-3.5 lg:py-5 cursor-pointer"
        @click="toggleShow('showInitiatorDetails')"
        v-if="transactionDetails.status === $STATUS.AWAITING_BUSINESS_APPROVAL"
      >
        <span>Initiator's Details</span>
        <span class="text-primary font-semibold text-xl">{{
          showInitiatorDetails ? '-' : '+'
        }}</span>
      </li>
      <transition name="slide-fade">
        <div v-if="showInitiatorDetails">
          <div class="pt-3">
            <div
              class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              v-for="(item, index) in transactionInitiatorDetails"
              :key="index"
            >
              <p class="text-sm font-thin">{{ item.label }}</p>
              <p class="text-sm font-normal text-black-10">
                {{ item.value }}
              </p>
            </div>
          </div>
        </div>
      </transition>

      <div v-if="isAwaitingDeposit">
        <el-button
          @click.native="handleDepositPage"
          class="w-full bg-primary p-5 mt-3 text-white cursor-pointer rounded-lg"
        >
          Make Deposit
        </el-button>
      </div>
    </ul>

    <!-- Buttons -->
    <div v-if="isAwaitingBusinessApproval" class="mt-8 flex flex-col gap-3">
      <el-tooltip
        :content="approveTooltipContent"
        placement="top"
        :disabled="isApproveTooltipDisabled"
      >
        <button
          @click="handleTransfer('approveTransfer')"
          class="action-button primary"
          :disabled="isApproverAccess"
        >
          Approve Transfer
        </button>
      </el-tooltip>

      <el-tooltip
        content="You do not have the access to cancel this transfer"
        placement="top"
        :disabled="hasApproverAccess"
      >
        <button
          @click="handleTransfer('cancelTransfer')"
          class="action-button outlined"
          :disabled="!hasApproverAccess"
        >
          Cancel Transfer
        </button>
      </el-tooltip>
    </div>
  </div>
</template>

<script>
import {
  MaskWalletAddressTwo,
  AddComma,
  ParseLocalTime,
  ParseMoney,
  ParseMoneyAll,
  ParseRelativeTime,
  GetCurrencyCountry,
  FormatServerName,
  StatusClass
} from '@/utils/helpers.js'
import userKinds from '@/mixins/userKinds.js'
import app from '@/mixins/app.js'

export default {
  name: 'FirstPage',
  mixins: [userKinds, app],
  props: {
    transactionDetails: {
      type: Object,
      default: () => {}
    },
    transferId: {
      type: String,
      default: () => ''
    },
    rateIncreased: {
      type: Boolean,
      default: () => false,
      required: true
    }
  },
  data () {
    return {
      showReasonForTransfer: false,
      showTransferDocument: false,
      showInitiatorDetails: false,
      isLoading: false,
      showDepositDetails: false,
      showBreakDown: false,
      documentsUploaded: [],
      imageUrl: null,
      fileName: '',
      uploadDocumentForm: {
        transfer_document: ''
      },
      formData: null
    }
  },
  components: {
    ArrowRightIcon: () => import('@/assets/v2/icons/ArrowRightIcon.vue'),
    MutedInfoIcon: () => import('@/assets/v2/icons/MutedInfoIcon.vue'),
    PdfIcon: () => import('@/assets/v2/icons/PdfIcon.vue'),
    Transition: () =>
      import('@/components/v2/animation/transition/Transition.vue'),
    EyeIcon: () => import('@/assets/icons/EyeIcon.vue'),
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue')
  },
  computed: {
    isHoldingsTransfer () {
      return this.transactionInfo.transferSource === 'treasury'
    },
    isAwaitingBusinessApproval () {
      return (
        this.transactionInfo.transferStatus ===
        this.$STATUS.AWAITING_BUSINESS_APPROVAL
      )
    },
    isApproveTooltipDisabled () {
      if (this.hasApproverAccess) {
        if (this.usdAmountLimit > 0) {
          return false
        } else {
          return true
        }
      } else {
        return false
      }
    },
    rate () {
      return this.exRate.find(
        (rate) =>
          rate.from === this.transactionDetails.depositedCurrency &&
          rate.to === 'USD'
      )?.exchange_rate
    },
    approveTooltipContent () {
      const amount =
        this.transactionDetails.depositedCurrency === 'USD'
          ? this.usdAmountLimit
          : this.usdAmountLimit * this.rate

      return this.usdAmountLimit > 0
        ? `Your transaction limit is insufficient to approve this transfer by ${this.parseMoneyAll(
            amount,
            this.transactionDetails.depositedCurrency
          )}`
        : 'You do not have the access to approve this transfer'
    },
    isWireTransferMethod () {
      return (
        this.transactionInfo.depositMethod ===
        this.$transactionType.WIRE_TRANSFER
      )
    },
    usdAmountLimit () {
      if (this.hasTransactionLimit) {
        const depositedCurrency = this.transactionDetails.depositedCurrency

        let amount
        if (depositedCurrency === 'USD') {
          amount = this.transactionDetails.depositedAmount
        } else {
          amount = this.transactionDetails.depositedAmount / this.rate
        }
        return amount - this.hasTransactionLimit
      } else {
        return 0
      }
    },
    isApproverAccess () {
      if (this.hasApproverAccess) {
        if (this.usdAmountLimit > 0) {
          return true
        } else {
          return false
        }
      } else {
        return true
      }
    },
    isAwaitingDeposit () {
      if (
        this.transactionInfo.depositStatus === this.$STATUS.PENDING ||
        this.transactionInfo.depositStatus === this.$STATUS.CANCELLED
      ) {
        return true
      }
      return false
    },
    transactionInitiatorDetails () {
      return [
        {
          label: 'Name',
          value: `${this.initiatorInfo.firstName || '-'}  ${
            this.initiatorInfo.lastName || '-'
          }`
        },
        {
          label: 'Email Address',
          value: this.initiatorInfo.email || '--'
        },
        {
          label: 'Time Initiated',
          value: this.transactionDetails.createdAt
            ? this.$dayjs(this.transactionDetails.createdAt).format(
                'DD MMM YYYY, h:mm A'
              )
            : '--'
        }
      ]
    },
    isCompletedDeposit () {
      return this.transactionInfo.depositStatus === this.$STATUS.COMPLETED
    },
    isStatusProcessing () {
      return this.transactionInfo.transferStatus === this.$STATUS.PROCESSING
    },
    transactionInfo () {
      return this.$store.state.transactions.transactionDetails
    },
    displayTimeDate () {
      const timeToPick =
        this.transactionInfo?.transferStatus === this.$STATUS.COMPLETED
          ? this.transactionInfo?.transferCompletedAt
          : this.transactionInfo?.transferCreatedAt

      return this.tooltipDateContent(timeToPick)
    },
    transferDocuments () {
      return this.transactionInfo.transferDocument || []
    },
    hasDocumentType () {
      return this.transferDocuments.some(
        (item) => item.type && item.fileURL === ''
      )
    },
    relevantDocuments () {
      return this.transferDocuments.filter((docs) => docs.fileURL)
    },
    hasRequestDocUploadArray () {
      return this.transferDocuments.filter(
        (item) => item.type && item.fileURL === ''
      )
    },
    transactionReference () {
      return this.isWireTransferMethod
        ? this.transactionInfo.transferInstruction.account_details
            .wireInstruction.reference
        : this.transactionInfo.depositReference
    }
  },
  methods: {
    hasPDF (file) {
      return file.includes('.pdf')
    },
    hasNonEmptyQuestions (document) {
      return document.questionnaire.some(
        (item) =>
          Object.prototype.hasOwnProperty.call(item, 'question') &&
          item.question !== ''
      )
    },
    parseRelativeTime (time) {
      return ParseRelativeTime(time)
    },
    computePayoutMethod (arg) {
      return arg.transferDestination?.bankDetails?.isMobileMoney === 'yes'
        ? 'Mobile Money'
        : 'Bank Transfer'
    },
    statusClass (status) {
      return StatusClass(status)
    },
    handleChange (documentID) {
      const fileInput = document.getElementById(documentID)
      const file = fileInput.files[0]
      if (file) {
        // this.fileName[documentID] = file.name
        this.fileName = file.name
      }
    },
    formatServerName (val) {
      let formattedName
      switch (val) {
        case this.$STATUS.AWAITING_BUSINESS_APPROVAL:
          formattedName = this.$STATUS.AWAITING_APPROVAL
          break
        default:
          formattedName = val
      }
      return FormatServerName(formattedName)
    },
    handleTransfer (actionType) {
      this.$emit('handleTransfer', actionType)
      this.handleClose()
    },
    convertToFormData (documentID) {
      const fileInput = document.getElementById(documentID)
      const file = fileInput.files[0]
      if (file) {
        // this.fileName[documentID] = file.name
        this.fileName = file.name
        this.formData = new FormData()
        this.formData.append('transfer_document', file)
      }
    },
    validateForm (uploadDocumentForm, documentID) {
      this.convertToFormData(documentID)
      this.uploadTransferDocument(documentID)
      this.resetForm()
    },
    resetForm () {
      Object.assign(this.uploadDocumentForm, {
        transfer_document: ''
      })
      this.fileName = ''
    },
    async uploadTransferDocument (documentID) {
      this.isLoading = true
      const vm = this
      await this.$store
        .dispatch('terminal/uploadTransferDocument', {
          formData: this.formData,
          documentID
        })
        .then(() => {
          vm.$message({
            showClose: true,
            message: 'uploaded successfully!',
            type: 'success'
          })
          this.handleClose()
        })
        .catch((error) => {
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    toggleShow (arg) {
      this[arg] = !this[arg]
    },
    maskID (val) {
      return MaskWalletAddressTwo(val)
    },
    handleClose () {
      this.$emit('close')
    },
    addComma (val) {
      return AddComma(val.toString())
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    parseLocalTime (time) {
      return ParseLocalTime(time)
    },
    showDepositDetailsHandler () {
      this.showDepositDetails = !this.showDepositDetails
    },
    parseMoney (amount) {
      return ParseMoney(amount)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    changePage () {
      this.$emit('changePage', 'second-page')
    },
    handleDepositPage () {
      this.$emit('changePage', 'deposit-details')
    },
    handleQuestionaireForm (document) {
      this.$emit('changePage', 'questionaire-form', document)
    },
    viewQuestionaireDetails (document) {
      this.$emit('changePage', 'questionaire-page', document)
    }
  },
  watch: {
    error: {
      immediate: true,
      handler: function handler (value) {
        this.validateMessage = value
        this.validateState = value ? 'error' : ''
      }
    },
    validateStatus: function validateStatus (value) {
      this.validateState = value
    }
  }
}
</script>

<style scoped>
.action-button {
  padding: 18px;
  font-size: 14px;
  border-radius: 10px;

  &:hover {
    opacity: 0.8;
  }
  &:active {
    transform: translateY(1px);
  }
}
.action-button.primary {
  background-color: var(--color-primary);
  color: #ffffff;

  &:disabled {
    background-color: #e4e4e7;
    color: #a5a7b6;
  }
}
.action-button.outlined {
  border: 1px solid #e43030;
  color: #d71841;

  &:disabled {
    border: 1px solid #e4e4e7;
    color: #a5a7b6;
  }
}
.disputed_status {
  border: 1px solid #df1642;
  color: #df1642;
  padding: 6px 10px;
  border-radius: 50px;
}
.attention-box {
  background: #ffecf0;
  border-radius: 4px;
  padding: 8px 10px 8px 18px;
  margin-bottom: 10px;
}
.text {
  color: #a51d3b;
}
>>> .el-input__inner {
  border-radius: 0.6rem;
  height: 3.5rem;
  padding-left: 1rem !important;
}
>>> .el-input__inner:hover,
>>> .el-input__inner:focus,
>>> .el-input__inner:active {
  border-color: var(--color-primary);
}
>>> .el-image {
  position: relative;
  display: block;
  overflow: hidden;
  margin: 20px 40px;
}
>>> input#fileInput.el-input__inner {
  color: transparent;
}
>>> .el-input__inner::file-selector-button {
  background: transparent;
  color: transparent;
  border: none;
  position: relative;
  width: 100% !important;
  z-index: 20 !important;
  cursor: pointer;
}
.browseButton {
  font-style: normal !important;
  font-weight: 400;
}
@media screen and (max-width: 768px) {
  >>> .el-form-item__label,
  .small-text,
  .browseButton,
  .el-input__inner {
    font-size: 0.75rem;
  }
}
</style>
